import { type SyncState } from '@sitewise/ui';
import { create } from 'zustand';

import type { BeforeInstallPromptEvent } from '../pwa/register-sw.js';

interface SyncStoreState {
  state: SyncState;
  isOnline: boolean;
  pendingCount: number;
  conflictCount: number;
  updateReady: boolean;
  applyUpdate: (() => void) | null;
  installPrompt: BeforeInstallPromptEvent | null;
  setState(next: SyncState): void;
  setOnline(online: boolean): void;
  setPendingCount(n: number): void;
  setConflictCount(n: number): void;
  setUpdateReady(ready: boolean): void;
  setApplyUpdate(fn: (() => void) | null): void;
  setInstallPrompt(event: BeforeInstallPromptEvent | null): void;
  /** Convenience: drive a `saving → saved → idle` flow around a mutation. */
  track<T>(promise: Promise<T>): Promise<T>;
  /** Recomputes `state` from `isOnline`, pendingCount, conflictCount. */
  recomputeState(): void;
}

function deriveState(
  online: boolean,
  pending: number,
  conflicts: number,
  current: SyncState,
): SyncState {
  if (conflicts > 0) return 'conflict';
  if (!online && pending > 0) return 'queued';
  if (!online) return 'offline';
  if (current === 'saving' || current === 'saved') return current;
  if (pending > 0) return 'syncing';
  return 'idle';
}

/**
 * Phase 10 — the sync store is now the single source of truth for the
 * indicator. Network status, queued mutations, and conflicts all feed
 * into a derived `state`. The store also owns the install prompt event
 * (so any UI can trigger it) and the PWA update-ready flag.
 */
export const useSyncStore = create<SyncStoreState>((set, get) => ({
  state: 'idle',
  isOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
  pendingCount: 0,
  conflictCount: 0,
  updateReady: false,
  applyUpdate: null,
  installPrompt: null,
  setState: (next) => set({ state: next }),
  setOnline: (online) => {
    set({ isOnline: online });
    get().recomputeState();
  },
  setPendingCount: (n) => {
    set({ pendingCount: Math.max(0, n) });
    get().recomputeState();
  },
  setConflictCount: (n) => {
    set({ conflictCount: Math.max(0, n) });
    get().recomputeState();
  },
  setUpdateReady: (ready) => set({ updateReady: ready }),
  setApplyUpdate: (fn) => set({ applyUpdate: fn }),
  setInstallPrompt: (event) => set({ installPrompt: event }),
  recomputeState: () => {
    const { isOnline, pendingCount, conflictCount, state } = get();
    set({ state: deriveState(isOnline, pendingCount, conflictCount, state) });
  },
  track: async (promise) => {
    set({ state: 'saving' });
    try {
      const result = await promise;
      set({ state: 'saved' });
      setTimeout(() => get().recomputeState(), 2000);
      return result;
    } catch (err) {
      get().recomputeState();
      throw err;
    }
  },
}));

// Module-level handle used by code that runs outside a React tree
// (service-worker registration, sync engine, etc.).
export const syncStore = useSyncStore;
